import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ScrollableRow from './ScrollableRow';
import { noweAktywne } from '@/lib/rzadFiltrow';

/**
 * JEDEN RZĄD FILTRÓW, JEDEN JĘZYK CHIPÓW (propozycja Maćka, zatwierdzona przez Piotra 6.10).
 *
 * Każdy aktywny filtr widać DOKŁADNIE RAZ, w tym rzędzie, jednym wyglądem: zielony obrys, miękkie
 * tło, ×. Bez „+N” i bez znacznika liczby przy „Wszystkie” — rząd jest licznikiem. Ten sam
 * komponent na telefonie i pulpicie.
 *
 * Kolejność stała: szybkie filtry (Cena, Pokoje, Metraż — także na telefonie), potem dwie sugestie (Metro 10 min, Ze zwierzęciem) w tym samym miejscu włączone
 * i wyłączone, potem reszta aktywnych filtrów w kolejności panelu.
 *
 * CHIPY PRZEWIJAJĄ SIĘ POD PRZYCISKAMI (plan E2.1 (2), Maciek): przypięte × i „Wszystkie” leżą
 * nad prawym końcem rzędu, rząd ma na końcu margines równy ich szerokości (ostatni chip da się
 * przewinąć przed nie), a chipy gasną gradientem tuż przed przyciskami. Do A8 obowiązywał wariant
 * Piotra z 6.10 — chipy kończą się PRZED przyciskami; zostaje pod tym samym przełącznikiem.
 */

/**
 * JEDYNE MIEJSCE WYBORU UKŁADU. `true` — rząd biegnie pod przyciskami do prawej krawędzi, z
 * marginesem na końcu i wygaszeniem przy przyciskach (plan E2.1 (2)). `false` — chipy kończą się
 * przed × i „Wszystkie” (wariant z 6.10).
 */
export const CHIPY_POD_PRZYCISKAMI = true;

/** Odstęp przy przewijaniu do nowo włączonego filtra — chip nie staje równo z krawędzią. */
const ODSTEP_PRZEWINIECIA_PX = 16;
export type WariantChipa = 'wylaczony' | 'aktywny' | 'sugestia';

type ChipProps = {
  wariant: WariantChipa;
  label: ReactNode;
  ikona?: ReactNode;
  /** Krzyżyk (tylko `aktywny`): zdejmuje ten jeden filtr. */
  naUsun?: () => void;
  ariaUsun?: string;
  kompakt?: boolean;
  testId?: string;
  /** Stały klucz w rzędzie (`data-chip`, `kluczMiejsca`) — po nim rząd poznaje nowo włączony filtr. */
  klucz?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

/**
 * Chip filtra. `ref` i pozostałe właściwości trafiają do PRZYCISKU TREŚCI, więc chip może być
 * wyzwalaczem okienka (`PopoverTrigger asChild`). Krzyżyk to osobny przycisk obok (nie w środku:
 * przycisk w przycisku jest niepoprawny), ze strefą dotyku 40 px.
 */
export const ChipFiltra = forwardRef<HTMLButtonElement, ChipProps>(function ChipFiltra(
  { wariant, label, ikona, naUsun, ariaUsun, kompakt = false, testId, klucz, className, ...reszta },
  ref,
) {
  const wys = kompakt ? 'h-8 text-xs' : 'h-10 text-sm';
  return (
    <span
      data-testid={testId}
      data-wariant={wariant}
      data-chip={klucz}
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-medium',
        wys,
        wariant === 'aktywny' && 'border-primary bg-primary/10 text-primary',
        // Wyłączona sugestia wygląda jak każdy wyłączony chip (plan E2.1 (6)): jeden język chipów,
        // różni je tylko strzałka — Cena rozwija okienko, sugestia włącza się jednym dotknięciem.
        (wariant === 'wylaczony' || wariant === 'sugestia') && 'border-input bg-white text-foreground',
        className,
      )}
    >
      <button
        ref={ref}
        type="button"
        {...reszta}
        className={cn(
          'strefa-dotyku relative inline-flex h-full items-center gap-1.5 rounded-full',
          kompakt ? 'px-3' : 'px-4',
          wariant === 'aktywny' && naUsun && (kompakt ? 'pr-1' : 'pr-1.5'),
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        {ikona}
        <span>{label}</span>
        {wariant === 'wylaczony' && <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />}
      </button>
      {wariant === 'aktywny' && naUsun && (
        <button
          type="button"
          onClick={naUsun}
          aria-label={ariaUsun}
          className={cn(
            'strefa-dotyku relative mr-1 flex shrink-0 items-center justify-center rounded-full text-primary/70 hover:bg-primary/15 hover:text-primary',
            kompakt ? 'h-6 w-6' : 'h-7 w-7',
          )}
        >
          <X className={kompakt ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        </button>
      )}
    </span>
  );
});

export function RzadFiltrow({
  children,
  naWyczysc,
  naWszystkie,
  kompakt = false,
  podPrzyciskami = CHIPY_POD_PRZYCISKAMI,
  zakladka,
}: {
  children: ReactNode;
  /** Pokazane tylko, gdy jest co czyścić (`null` = brak przycisku). */
  naWyczysc: (() => void) | null;
  naWszystkie: () => void;
  /** Telefon: niższe chipy, okrągłe przyciski, bez przewijania myszą. */
  kompakt?: boolean;
  /** Tylko do testów i porównań; w aplikacji decyduje `CHIPY_POD_PRZYCISKAMI`. */
  podPrzyciskami?: boolean;
  /**
   * Flaga „Moje filtry” i „Zapisz” (C1, `ZakladkaMojichFiltrow`). Pulpit: prawa część przycisku
   * dzielonego [Wszystkie | flaga | Zapisz] w jednym obramowaniu; telefon: własna pigułka za
   * okrągłym przyciskiem filtrów. Gość, właściciel i agent: brak (`undefined`).
   */
  zakladka?: ReactNode;
}) {
  const { t } = useTranslation();
  const okragly = kompakt ? 'h-8 w-8' : 'h-10 w-10';
  const rzad = useRef<HTMLDivElement | null>(null);
  const refRzedu = useCallback((el: HTMLDivElement | null) => { rzad.current = el; }, []);
  const przyciski = useRef<HTMLDivElement>(null);
  const [szerPrzyciskow, setSzerPrzyciskow] = useState(0);

  // PRZEWINIĘCIE DO NOWO WŁĄCZONEGO FILTRA (propozycja Maćka): włączenie z panelu albo z sugestii
  // nie zostawia człowieka na początku rzędu, szukającego, gdzie filtr poszedł.
  const poprzednie = useRef<Set<string> | null>(null);
  useEffect(() => {
    const el = rzad.current;
    if (!el) return;
    const chipy = [...el.querySelectorAll<HTMLElement>('[data-chip]')];
    const obecne = chipy.map((c) => ({ klucz: c.dataset.chip!, aktywny: c.dataset.wariant === 'aktywny' }));
    const nowe = noweAktywne(poprzednie.current, obecne);
    poprzednie.current = new Set(obecne.filter((o) => o.aktywny).map((o) => o.klucz));
    const cel = chipy.find((c) => c.dataset.chip === nowe[nowe.length - 1]);
    if (!cel) return;
    const r = el.getBoundingClientRect();
    const c = cel.getBoundingClientRect();
    const widoczny = r.right - (podPrzyciskami ? szerPrzyciskow : 0);
    if (c.right > widoczny) el.scrollLeft += c.right - widoczny + ODSTEP_PRZEWINIECIA_PX;
    else if (c.left < r.left) el.scrollLeft -= r.left - c.left + ODSTEP_PRZEWINIECIA_PX;
  });

  useLayoutEffect(() => {
    if (!podPrzyciskami || !przyciski.current) return;
    const el = przyciski.current;
    const zmierz = () => setSzerPrzyciskow(el.offsetWidth);
    zmierz();
    const ro = new ResizeObserver(zmierz);
    ro.observe(el);
    return () => ro.disconnect();
  }, [podPrzyciskami]);

  const grupaPrzyciskow = (
    <div
      ref={przyciski}
      className={cn(
        'flex shrink-0 items-center gap-2',
        podPrzyciskami && 'absolute inset-y-0 right-0 bg-white pl-2',
      )}
    >
      {/* Chipy pod przyciskami gasną TUŻ PRZED nimi — wygaszenie ScrollableRow leży przy krawędzi
          rzędu, czyli pod przyciskami, i nie byłoby go widać. */}
      {podPrzyciskami && (
        <span
          aria-hidden
          data-testid="wygaszenie-przy-przyciskach"
          className={cn(
            'pointer-events-none absolute inset-y-0 right-full bg-gradient-to-l from-white from-20% to-transparent',
            // Makieta (`.scroller::after`): widoczne wygaszenie ~64 px na pulpicie i ~48 px na
            // telefonie, z pełnym kolorem tuż przy przyciskach; 24 px gasiło chip za ostro.
            kompakt ? 'w-12' : 'w-16',
          )}
        />
      )}
      {/* × ma miejsce ZAWSZE (Piotr 7.10): pojawiający się × zwężał rząd i przesuwał jego krawędź.
          Bez filtrów jest niewidoczny, wyłączony i poza kolejnością Tab. */}
      {naWyczysc ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={naWyczysc}
              aria-label={t('search.clear_all_aria')}
              data-testid="wyczysc-wszystkie"
              className={cn(
                'strefa-dotyku relative flex shrink-0 items-center justify-center rounded-full border border-input bg-white text-foreground hover:bg-muted',
                okragly,
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('search.clear_all_aria')}</TooltipContent>
        </Tooltip>
      ) : (
        <span aria-hidden="true" data-testid="wyczysc-wszystkie-miejsce" className={cn('invisible shrink-0', okragly)} />
      )}
      {zakladka && !kompakt ? (
        // PRZYCISK DZIELONY (makieta serp-saved-filters-proto.html): jedno obramowanie,
        // „Wszystkie” po lewej, flaga i wysuwane „Zapisz” po prawej.
        <span data-testid="przycisk-dzielony" className="group/dzielony inline-flex h-10 shrink-0 items-stretch rounded-full border border-input bg-white">
          {/* Podpowiedź jak przy fladze i „Zapisz” (pod spodem, bez „treści do najechania”), Maciek 19.10. */}
          <Tooltip disableHoverableContent>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={naWszystkie}
                aria-label={t('search.all_filters_button')}
                data-testid="wszystkie-filtry"
                // Po zapisie „Zapisz” się chowa i „Wszystkie” wjeżdża pod nieruchomy kursor: wtedy bez
                // podświetlenia, póki flaga trzyma `data-wyciszone` (ZakladkaMojichFiltrow, Maciek 19.10).
                className="strefa-dotyku relative flex shrink-0 items-center justify-center gap-2 rounded-l-full px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-has-[[data-wyciszone=tak]]/dzielony:hover:bg-transparent"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {t('search.all_short')}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="data-[state=closed]:!animate-none" data-testid="podpowiedz-wszystkie">{t('search.all_filters_tip')}</TooltipContent>
          </Tooltip>
          {zakladka}
        </span>
      ) : (
        // Telefon: bez hovera, bez podpowiedzi. Pulpit gościa (bez flagi): ta sama podpowiedź co wyżej.
        (() => {
          const przycisk = (
            <button
              type="button"
              onClick={naWszystkie}
              aria-label={t('search.all_filters_button')}
              data-testid="wszystkie-filtry"
              className={cn(
                'strefa-dotyku relative flex shrink-0 items-center justify-center gap-2 rounded-full border border-input bg-white font-medium text-foreground hover:bg-muted',
                kompakt ? okragly : 'h-10 px-4 text-sm',
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {!kompakt && t('search.all_short')}
            </button>
          );
          return kompakt ? przycisk : (
            <Tooltip disableHoverableContent>
              <TooltipTrigger asChild>{przycisk}</TooltipTrigger>
              <TooltipContent side="bottom" className="data-[state=closed]:!animate-none" data-testid="podpowiedz-wszystkie">{t('search.all_filters_tip')}</TooltipContent>
            </Tooltip>
          );
        })()
      )}
      {kompakt && zakladka}
    </div>
  );

  return (
    <div
      className={cn('flex items-center gap-2', podPrzyciskami && 'relative')}
      data-testid="rzad-filtrow-caly"
      data-pod-przyciskami={podPrzyciskami ? 'tak' : 'nie'}
    >
      <div className="min-w-0 flex-1">
        {/* `py-1 -my-1`: rząd przewija się w bok, a `overflow-x: auto` przycina też w pionie —
            bez zapasu strefa dotyku (40 px) byłaby ucięta. */}
        <ScrollableRow testId="rzad-filtrow" className="-my-1 flex items-center gap-2 py-1" mysz={!kompakt} refRzedu={refRzedu}>
          {children}
          {/* Wariant „pod przyciskami”: margines na końcu = szerokość przycisków, więc ostatni
              chip da się przewinąć przed nie. */}
          {podPrzyciskami && <span aria-hidden className="shrink-0" style={{ width: szerPrzyciskow }} />}
        </ScrollableRow>
      </div>
      {grupaPrzyciskow}
    </div>
  );
}

export default RzadFiltrow;
